import './Selector.modules.scss';
import Select, { components, createFilter, GroupBase, SingleValue, MultiValue, StylesConfig, OnChangeValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ReactNode, useCallback } from 'react';
import TooltipContainer from '../TooltipContainer/TooltipContainer';
import { ReactComponent as ErrorIcon } from '../../../assets/svg/errorIcon.svg';
import StyledCheckbox from '../StyledCheckbox/StyledCheckbox';
import React from 'react';
import { MultiValueComponent, ValueContainerWithOverflowTip } from './selectorUtil';
import VirtualizedMenuList from './VirtualizedMenuList';
import { useTranslation } from 'react-i18next';

export type SelectorType = {
  label: string,
  value: string,
  __isNew__?: boolean,

} | null

export type Props<T extends SelectorType, IsMulti extends boolean> = {
  options: readonly (T | GroupBase<T>)[],
  value: IsMulti extends true ? MultiValue<T> : SingleValue<T>,
  onChange?: (value: OnChangeValue<T, IsMulti>) => void,
  isCreatable?: boolean,
  styles?: StylesConfig<T, IsMulti, GroupBase<T>>,
  isSearchable?: boolean,
  className?: string,
  placeholder?: string,
  mini?: boolean,
  formatCreateLabel?: (value: string) => ReactNode,
  formatOptionLabel?: (option: T, { context }: { context: 'menu' | 'value' }) => ReactNode,
  required?: boolean,
  label?: string | ReactNode,
  errorMessage?: string,
  isOptionDisabled?: (x:T) => boolean,
  getSearchString?: (data: T) => string,
  /**
   * Render only the options in view, filling the rest in on scroll. react-select mounts
   * a whole menu at once, so a list in the thousands stalls on open. Worth it from a few
   * hundred options; below that it only adds a scroll container.
   */
  virtualized?: boolean,
  components?: any,
  isMulti?: IsMulti,
  isOverflow?: boolean,
  isClearable?: boolean,
  isDisabled?: boolean,
  isSelected?: (value: T) => boolean,
  style?: React.CSSProperties,
  readOnly?: boolean,
  onBlur?: (event: any) => void,
  onInputChange?: (value: string, meta: any) => void,
}

const Selector = <T extends SelectorType, IsMulti extends boolean = false>({ 
  options, 
  value, 
  onChange, 
  isCreatable, 
  isDisabled, 
  styles, 
  isSearchable, 
  mini, 
  formatCreateLabel,
  formatOptionLabel, 
  className, 
  label, 
  required, 
  errorMessage, 
  isMulti,
  isSelected,
  readOnly,
  isClearable,
  getSearchString,
  virtualized,
  ...props
}: Props<T, IsMulti>) => {
  
  const [t] = useTranslation('shared', { keyPrefix: 'selector' })

  const IndicatorContainerWithErrorMessage = useCallback((props: any) => (
      <components.IndicatorsContainer {...props}>
        {errorMessage && (
          <TooltipContainer renderReferenceComponent={(className, ref) => <ErrorIcon style={{ marginRight: '5px' }} className={className} ref={ref} />}>
            <span>{errorMessage}</span>
          </TooltipContainer>
        )}
        {props.children}
      </components.IndicatorsContainer>
  ), [errorMessage]);

  const SelectContainerWithLabel = useCallback((props: any) => (
    <components.SelectContainer {...props}>
      {label && <div className="selector-label">{label} {required ? <span className="selector-label-required">*</span> : ''} </div>}
      {props.children}
    </components.SelectContainer>
  ), [label, required]);

  const OptionWithStyledCheckbox = useCallback((props: any) => {
    return (
      <components.Option {...props}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {props.data.__isNew__ ? props.children : (
            <>
              <StyledCheckbox disabled={readOnly} checked={isSelected ? isSelected(props.data) : props.isSelected} />
              <span>{props.label}</span>
            </>
          )}
        </span>
      </components.Option>
    );
  }, [isSelected]);

  const mergedComponents = {
    IndicatorsContainer: IndicatorContainerWithErrorMessage,
    SelectContainer: SelectContainerWithLabel,
    MultiValueComponent,
    ...(isMulti && !formatOptionLabel ? { Option: OptionWithStyledCheckbox } : {}),
    ...(props.isOverflow ? { ValueContainer: ValueContainerWithOverflowTip } : {}),
    ...(virtualized ? { MenuList: VirtualizedMenuList } : {}),
    ...props.components,
  };

  const customStyles: StylesConfig<T, IsMulti> = {
    option: (provided, state) => {
      const isThisOptionDisabled = readOnly || state.isDisabled;
      const newStyles = {
        ...provided,
        backgroundColor: state.isFocused && !isThisOptionDisabled ? 'var(--gray-100)' : 'white',
        color: isThisOptionDisabled ? 'var(--gray-300)' : 'var(--gray-700)',
        cursor: readOnly ?
          'not-allowed' :
          (isThisOptionDisabled ? 'default' : 'pointer'),
        padding: '8px 12px',
        '&:hover': {
          backgroundColor: isThisOptionDisabled ? 'white' : 'var(--gray-100)',
        },
        ...styles?.option?.(provided, state),
      };

      if (styles?.option) {
        return styles.option(newStyles, state);
      } else {
        return newStyles;
      }
    },
    multiValue: (provided, state) => {
      const newStyles = {
        ...provided,
        backgroundColor: 'var(--gray-100)',
        borderRadius: '4px',
        cursor: readOnly ? 'not-allowed' : 'pointer',
        flexShrink: 0,
      };

      if (styles?.multiValue) {
        return styles.multiValue(newStyles, state);
      } else {
        return newStyles;
      }
    },
    multiValueLabel: (provided, state) => {
      const newStyles = {
        ...provided,
        color: 'var(--gray-700)',
        padding: '4px 8px',
        cursor: readOnly ? 'not-allowed' : 'pointer',
        ...(props.isOverflow ? { overflow: 'visible' as const, textOverflow: 'clip' as const } : {}),
      };
      if (styles?.multiValueLabel) {
        return styles.multiValueLabel(newStyles, state);
      } else {
        return newStyles;
      }
    },
    multiValueRemove: (provided, state) => {
      const newStyles = {
        ...provided,
        color: 'var(--gray-500)',
        ':hover': {
          backgroundColor: 'var(--gray-200)',
          color: 'var(--gray-700)',
        },
        cursor: readOnly ? 'not-allowed' : 'pointer'
      };
      if (styles?.multiValueRemove) {
        return styles.multiValueRemove(newStyles, state);
      } else {
        return newStyles;
      }
    },
    control: (provided, state) => {
      const newStyles = {
        ...provided,
        backgroundColor: 'white',
        borderColor: state.isFocused ? 'var(--gray-500)' : 'var(--gray-300)',
        boxShadow: state.isFocused ? '0 0 0 1px var(--gray-500)' : 'none',
        '&:hover': {
          borderColor: 'var(--gray-400)',
        },
        cursor: readOnly ? 'not-allowed' : 'pointer',
        ...(props.isOverflow ? { flexWrap: 'nowrap' as const, overflow: 'hidden' as const } : {}),
      };
      if (styles?.control) {
        return styles.control(newStyles, state);
      } else {
        return newStyles;
      }
    },
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      cursor: readOnly ? 'not-allowed' : 'pointer'
    }),
    // Stacking context for the menu when it is rendered into a portal
    // (menuPortalTarget). Without this the portal layer defaults to z-index 1
    // and the menu would render behind other content.
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const mergedStyles = {
    valueContainer: (provided : any) =>  {
      const valueContainerStyles: {[x:string]: string} = {
        ...provided,
      };
      if (isMulti) {
        valueContainerStyles.overflow = 'hidden';
        if (props.isOverflow) {
          valueContainerStyles.flexWrap = 'nowrap';
        }
      } else {
        valueContainerStyles.height = isSearchable ? provided.height : '28px';
      }
      return valueContainerStyles;
    },
    ...customStyles,
  };

  const filterOption = isSearchable ? createFilter({
    stringify: option => getSearchString ? getSearchString(option.data) : option.label.toString()
  }) : undefined;

  const selectElement = isCreatable ? (
    <CreatableSelect<T, IsMulti>
      placeholder={!readOnly && t('placeholder', 'Välj')}
      {...props}
      isSearchable={!readOnly && isSearchable}
      isClearable={!!(isClearable && !readOnly)}
      className={`accSelector-container ${mini ? 'mini' : ''} ${errorMessage ? 'selector-error' : ''} ${className || ''} ${readOnly ? 'accSelector-readOnly' : ''}`}
      classNamePrefix="accSelector"
      options={options}
      value={value}
      onChange={(value) => {
        if (readOnly) {
          return;
        }
        onChange?.(value);
      }}
      styles={mergedStyles}
      filterOption={filterOption}
      // react-select's ScrollCaptor measures the menu list and preventDefaults a wheel
      // that would scroll past it. A virtualised menu scrolls an inner element it knows
      // nothing about, so capture has to go or the wheel never reaches it.
      {...(virtualized ? { captureMenuScroll: false } : {})}
      components={mergedComponents}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      closeMenuOnSelect={!isMulti}
      isMulti={isMulti as IsMulti}
      isDisabled={isDisabled}
      formatCreateLabel={formatCreateLabel ?? ((userInput) => t('createLabel', 'Skapa "{{userInput}}"', { userInput }))}
      formatOptionLabel={formatOptionLabel}
      noOptionsMessage={() => t('emptyMessage', 'Inga alternativ')}
    />
  ) : (
    <Select<T, IsMulti>
      placeholder={!readOnly && t('placeholder', 'Välj')}
      {...props}
      isSearchable={!readOnly && isSearchable}
      isClearable={!!(isClearable && !readOnly)}
      className={`accSelector-container ${mini ? 'mini' : ''} ${errorMessage ? 'selector-error' : ''} ${className || ''} ${readOnly ? 'accSelector-readOnly' : ''}`}
      classNamePrefix="accSelector"
      options={options}
      value={value}
      onChange={(value) => {
        if (readOnly) {
          return;
        }
        onChange?.(value);
      }}
      styles={mergedStyles}
      filterOption={filterOption}
      // react-select's ScrollCaptor measures the menu list and preventDefaults a wheel
      // that would scroll past it. A virtualised menu scrolls an inner element it knows
      // nothing about, so capture has to go or the wheel never reaches it.
      {...(virtualized ? { captureMenuScroll: false } : {})}
      components={mergedComponents}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      closeMenuOnSelect={!isMulti}
      isMulti={isMulti as IsMulti}
      isDisabled={isDisabled}
      formatOptionLabel={formatOptionLabel}
      noOptionsMessage={() => t('emptyMessage', 'Inga alternativ')}
    />
  );

  if (props.isOverflow) {
    return (
      <div
        className={`accSelector-outer-wrapper${className ? ' ' + className : ''}`}
        style={props.style}
      >
        {selectElement}
      </div>
    );
  }
  return selectElement;
};

export default Selector;
